import jsPDF from 'jspdf';
import { getFullUrl } from '../modules/media/urlUtils';

interface PDFGeneratorOptions {
  margin: number;
  fontSize: {
    title: number;
    subtitle: number;
    body: number;
    small: number;
  };
  colors: {
    primary: [number, number, number];
    secondary: [number, number, number];
    text: [number, number, number];
    lightGray: [number, number, number];
  };
}

const DEFAULT_OPTIONS: PDFGeneratorOptions = {
  margin: 15,
  fontSize: {
    title: 24,
    subtitle: 16,
    body: 11, // Increased for better readability
    small: 10, // Minimum 10pt as requested
  },
  colors: {
    primary: [124, 58, 237], // Purple-600
    secondary: [139, 92, 246], // Purple-500
    text: [31, 41, 55], // Gray-800
    lightGray: [243, 244, 246], // Gray-100
  },
};

export class PDFGenerator {
  private doc: jsPDF;
  private options: PDFGeneratorOptions;
  private cursorY: number;
  private pageWidth: number;
  private pageHeight: number;
  private contentWidth: number;

  constructor(options: Partial<PDFGeneratorOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - (this.options.margin * 2);
    this.cursorY = this.options.margin;

    this.setupDocument();
  }

  private setupDocument() {
    this.doc.setProperties({
      title: 'SoArtistic Account Export',
      subject: 'Personal Data Export',
      author: 'SoArtistic Platform',
      creator: 'SoArtistic PDF Generator',
    });
  }

  private async fetchImage(url: string): Promise<{ data: string; format: string } | null> {
    try {
      const fullUrl = getFullUrl(url);
      if (!fullUrl) return null;

      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const contentType = response.headers.get('content-type') || '';
      
      let format = 'JPEG';
      if (contentType.includes('png')) format = 'PNG';
      else if (contentType.includes('webp')) format = 'WEBP';
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({
          data: reader.result as string,
          format
        });
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Failed to load image:', url, error);
      return null;
    }
  }

  private checkPageBreak(heightNeeded: number) {
    if (this.cursorY + heightNeeded > this.pageHeight - this.options.margin) {
      this.doc.addPage();
      this.cursorY = this.options.margin;
      return true;
    }
    return false;
  }

  private addHeader(text: string) {
    this.doc.setFontSize(10);
    this.doc.setTextColor(...this.options.colors.text);
    this.doc.text(text, this.pageWidth / 2, 10, { align: 'center' });
  }

  private addFooter(pageNumber: number, totalPages: number) {
    this.doc.setFontSize(8);
    this.doc.setTextColor(150, 150, 150);
    this.doc.text(
      `Page ${pageNumber} of ${totalPages} - Generated on ${new Date().toLocaleDateString()}`,
      this.pageWidth / 2,
      this.pageHeight - 10,
      { align: 'center' }
    );
  }

  public addTitle(text: string) {
    this.checkPageBreak(20);
    this.doc.setFontSize(this.options.fontSize.title);
    this.doc.setTextColor(...this.options.colors.primary);
    this.doc.text(text, this.options.margin, this.cursorY + 8);
    this.cursorY += 15;
    
    // Decorative line
    this.doc.setDrawColor(...this.options.colors.secondary);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.options.margin, this.cursorY, this.options.margin + 50, this.cursorY);
    this.cursorY += 10;
  }

  public addSectionTitle(text: string) {
    this.checkPageBreak(15);
    this.cursorY += 5;
    this.doc.setFontSize(this.options.fontSize.subtitle);
    this.doc.setTextColor(...this.options.colors.text);
    this.doc.text(text, this.options.margin, this.cursorY + 6);
    this.cursorY += 12;
  }

  public addText(text: string, fontSize = this.options.fontSize.body, color = this.options.colors.text) {
    this.doc.setFontSize(fontSize);
    this.doc.setTextColor(...color);
    
    const lines = this.doc.splitTextToSize(text, this.contentWidth);
    const height = lines.length * (fontSize * 0.45); // Approximate height per line
    
    this.checkPageBreak(height);
    this.doc.text(lines, this.options.margin, this.cursorY + (fontSize * 0.35));
    this.cursorY += height + 2;
  }

  public async addCard(data: Record<string, string>, imageUrl?: string, title?: string) {
    const cardPadding = 5;
    const cardWidth = this.contentWidth;
    let cardHeight = 10; // Initial padding

    // Calculate height
    if (title) cardHeight += 8;
    
    const textLines: { label: string; value: string[]; height: number }[] = [];
    
    // Process text content to calculate height
    let textYOffset = 0;
    const labelWidth = 40;
    const valueWidth = cardWidth - (imageUrl ? 60 : 0) - labelWidth - (cardPadding * 2);

    Object.entries(data).forEach(([key, value]) => {
      if (!value) return;
      const wrappedValue = this.doc.splitTextToSize(value, valueWidth);
      const lineHeight = wrappedValue.length * 5; // 5mm per line approx for size 10
      textLines.push({ label: key, value: wrappedValue, height: lineHeight });
      textYOffset += lineHeight + 2;
    });

    cardHeight += textYOffset;
    
    // Minimum height if image exists
    if (imageUrl) {
      cardHeight = Math.max(cardHeight, 50); // Min height for image card
    }

    this.checkPageBreak(cardHeight + 5);

    // Draw Card Background
    this.doc.setFillColor(...this.options.colors.lightGray);
    this.doc.roundedRect(this.options.margin, this.cursorY, cardWidth, cardHeight, 3, 3, 'F');

    let currentY = this.cursorY + cardPadding + 5;
    const currentX = this.options.margin + cardPadding;

    if (imageUrl) {
      try {
        const imgResult = await this.fetchImage(imageUrl);
        if (imgResult) {
          const imgSize = 40;
          const imgX = this.options.margin + cardWidth - imgSize - cardPadding;
          const imgY = this.cursorY + cardPadding;
          
          const props = this.doc.getImageProperties(imgResult.data);
          const ratio = props.width / props.height;
          let w = imgSize;
          let h = imgSize;
          
          if (ratio > 1) {
            h = w / ratio;
          } else {
            w = h * ratio;
          }

          this.doc.addImage(imgResult.data, imgResult.format, imgX + (imgSize - w)/2, imgY + (imgSize - h)/2, w, h);
        }
      } catch (_e) {
        void _e;
      }
    }

    // Draw Title
    if (title) {
      this.doc.setFontSize(12);
      this.doc.setTextColor(...this.options.colors.primary);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(title, currentX, currentY);
      currentY += 8;
    }

    // Draw Fields
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    textLines.forEach(line => {
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(line.label + ':', currentX, currentY);
      
      this.doc.setTextColor(...this.options.colors.text);
      this.doc.text(line.value, currentX + labelWidth, currentY);
      
      currentY += line.height + 2;
    });

    this.cursorY += cardHeight + 5; // Margin bottom
  }

  public async addPostGrid(posts: Array<Record<string, unknown>>) {
    const cols = 2;
    const gap = 5;
    const colWidth = (this.contentWidth - (gap * (cols - 1))) / cols;
    
    for (let i = 0; i < posts.length; i += cols) {
      const rowItems = posts.slice(i, i + cols);

      const renderItem = async (post: Record<string, unknown>) => {
        const padding = 4;
        let h = padding;
        
        // Image
        let imgHeight = 0;
        let imgResult: { data: string; format: string } | null = null;
        if (post.media_files?.[0]?.url) {
            imgHeight = 40;
            imgResult = await this.fetchImage(post.media_files[0].url);
        }

        h += imgHeight;
        if (imgHeight > 0) h += 4;

        // Title
        const titleLines = this.doc.splitTextToSize(post.title || 'Untitled', colWidth - (padding * 2));
        const titleHeight = titleLines.length * 5;
        h += titleHeight + 2;

        // Date
        h += 5; // Date height

        return { h, imgResult, titleLines };
      };

      const renderedItems = await Promise.all(rowItems.map(async (p) => {
          return await renderItem(p);
      }));

      const rowHeight = Math.max(...renderedItems.map(r => r.h)) + 10;
      
      this.checkPageBreak(rowHeight);

      // Second pass: Render
      for (let j = 0; j < rowItems.length; j++) {
        const item = rowItems[j];
        const rendered = renderedItems[j];
        const itemX = this.options.margin + (j * (colWidth + gap));
        const itemY = this.cursorY;

        // Background
        this.doc.setFillColor(255, 255, 255);
        this.doc.setDrawColor(230, 230, 230);
        this.doc.roundedRect(itemX, itemY, colWidth, rowHeight - 5, 2, 2, 'FD');

        let contentY = itemY + 5;

        // Image
        if (rendered && rendered.imgResult && rendered.imgResult.data) {
            try {
                // Center crop/fit
                const imgW = colWidth - 8;
                const imgH = 40;
                const props = this.doc.getImageProperties(rendered.imgResult.data);
                const ratio = props.width / props.height;
                // Simple fit centered
                let drawW = imgW;
                let drawH = imgW / ratio;
                if (drawH > imgH) {
                    drawH = imgH;
                    drawW = drawH * ratio;
                }
                
                const format = rendered.imgResult.format || 'JPEG';
                // Only allow supported formats to prevent "UNKNOWN" error
                if (['JPEG', 'PNG', 'WEBP'].includes(format)) {
                    this.doc.addImage(rendered.imgResult.data, format, itemX + 4 + (imgW-drawW)/2, contentY, drawW, drawH);
                }
            } catch (e) {
                console.warn('Error adding image to PDF:', e);
            }
            contentY += 44;
        }

        // Title
        this.doc.setFontSize(10);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(...this.options.colors.text);
        this.doc.text(rendered.titleLines, itemX + 4, contentY);
        contentY += (rendered.titleLines.length * 5) + 2;

        // Date
        this.doc.setFontSize(this.options.fontSize.small);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(150, 150, 150);
        this.doc.text(new Date(item.created_at).toLocaleDateString(), itemX + 4, contentY);
      }

      this.cursorY += rowHeight;
    }
  }

  public addList(items: string[]) {
      items.forEach(item => {
          this.checkPageBreak(8);
          this.doc.setFillColor(...this.options.colors.secondary);
          this.doc.circle(this.options.margin + 2, this.cursorY - 1, 1, 'F');
          
          this.doc.setFontSize(this.options.fontSize.body);
          this.doc.setTextColor(...this.options.colors.text);
          this.doc.text(item, this.options.margin + 6, this.cursorY);
          this.cursorY += 6;
      });
      this.cursorY += 5;
  }

  public async addReceipt(data: {
    orderId: string;
    date: string;
    buyerName: string;
    buyerEmail: string;
    items: Array<{
      title: string;
      price: number;
      artistName: string;
    }>;
    total: number;
  }) {
    // Header
    this.addTitle('Comprobante de Venta');
    
    // Order Info
    this.addText(`Referencia: ${data.orderId}`);
    this.addText(`Fecha: ${new Date(data.date).toLocaleDateString()}`);
    this.cursorY += 5;

    // Buyer Info
    this.addSectionTitle('Datos del Comprador');
    this.addText(`Nombre: ${data.buyerName}`);
    this.addText(`Email: ${data.buyerEmail}`);
    this.cursorY += 10;

    // Items
    this.addSectionTitle('Detalle de la Compra');
    
    // Table Header
    const startY = this.cursorY;
    this.doc.setFillColor(245, 245, 245);
    this.doc.rect(this.options.margin, startY - 5, this.contentWidth, 10, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Descripción', this.options.margin + 2, startY);
    this.doc.text('Precio', this.pageWidth - this.options.margin - 2, startY, { align: 'right' });
    
    this.cursorY += 10;

    // Items List
    data.items.forEach(item => {
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`${item.title} (Artista: ${item.artistName})`, this.options.margin + 2, this.cursorY);
      this.doc.text(`${item.price.toFixed(2)} €`, this.pageWidth - this.options.margin - 2, this.cursorY, { align: 'right' });
      this.cursorY += 8;
    });

    // Total
    this.cursorY += 5;
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(this.options.margin, this.cursorY, this.pageWidth - this.options.margin, this.cursorY);
    this.cursorY += 10;
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Total', this.pageWidth - this.options.margin - 40, this.cursorY);
    this.doc.text(`${data.total.toFixed(2)} €`, this.pageWidth - this.options.margin - 2, this.cursorY, { align: 'right' });
    
    this.cursorY += 20;
    
    // Footer Note
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(100, 100, 100);
    const note = 'Este documento es un comprobante de venta. SoArtistic actúa como intermediario en esta transacción. El vendedor (artista) es el responsable de la venta.';
    const splitNote = this.doc.splitTextToSize(note, this.contentWidth);
    this.doc.text(splitNote, this.options.margin, this.cursorY);
  }

  public getOutput(): string {
    return this.doc.output('datauristring').split(',')[1];
  }

  public save(filename: string) {
    const pageCount = this.doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      this.addFooter(i, pageCount);
    }
    this.doc.save(filename);
  }
}
